import { useEffect } from "react";

import filterAttachmentsByLanguage from "@/view/utils/filterAttachmentsByLanguage";
import groupAttachmentsBySubjectType from "@/view/utils/groupAttachmentsBySubjectType";
import useReadingSkillsContext from "@/view/utils/useReadingSkillsContext/useReadingSkillsContext";

const ELA = "ELA";

/**
 * Parses data and groups attachments into subject areas and returns useful
 * variables such as the ELA quiz (used for Reading Skill features), and
 * attachments grouped by subject for use on the activity panel.
 *
 * @param {Object} activityToLevelMapping
 * @param {Array<Object>} attachments
 * @param {Object} currentLevel
 * @param {boolean} isTeacher
 * @param {Object} selectedAssignment
 * @returns {Object}
 */
const useGroupAttachments = ({
  activityToLevelMapping,
  attachments,
  currentLevel,
  isTeacher,
  selectedAssignment,
}) => {
  const getElaQuiz = (attachments) =>
    attachments[ELA]?.find((activity) => activity.content_type === "QUIZ");
  const getAllElaQuizzes = (attachments) =>
    attachments[ELA]?.filter((activity) => activity.content_type === "QUIZ");
  // Grouped attachments based on current language
  const filteredAttachmentsByLanguage = filterAttachmentsByLanguage(
    attachments,
    currentLevel?.language,
  );
  const attachmentsGroupedBySubject = groupAttachmentsBySubjectType(
    filteredAttachmentsByLanguage,
  );
  const currentElaQuiz = getElaQuiz(attachmentsGroupedBySubject);
  const currentElaQuizQuestionIds = activityToLevelMapping?.[
    currentElaQuiz?.id
  ]?.questions.map((question) => question.id);

  // Grouped attachments including all languages levels
  const allAttachmentsGroupedBySubject =
    groupAttachmentsBySubjectType(attachments);
  const allElaQuizzes = getAllElaQuizzes(allAttachmentsGroupedBySubject);

  const {
    currentElaLeveledQuizId,
    getQuizQuestionMetadataAndSetState,
    quizQuestionMetadata,
  } = useReadingSkillsContext();

  // This approach of calling the metadata endpoint for each quiz question will be temporary until
  // the content-relations api is available via the proxy endpoint, in which then we will only need
  // to make a single api call for all question ids.
  useEffect(() => {
    const hasLevelChanged =
      currentElaLeveledQuizId !==
      activityToLevelMapping?.[currentElaQuiz?.id]?.id;

    if (
      isTeacher &&
      currentElaQuizQuestionIds?.length &&
      (hasLevelChanged || !quizQuestionMetadata)
    ) {
      getQuizQuestionMetadataAndSetState({
        allElaQuizzes,
        currentElaQuizQuestionIds,
        currentElaQuizId: activityToLevelMapping?.[currentElaQuiz?.id]?.id,
        activityElementSelections:
          selectedAssignment?.activity_element_selections,
        selectedAssignment,
      });
    }
  }, [
    currentLevel?.id,
    currentElaQuizQuestionIds,
    selectedAssignment?.activity_element_selections,
    selectedAssignment?.activity_members,
  ]);

  return {
    attachmentsGroupedBySubject,
    filteredAttachmentsByLanguage,
  };
};

export default useGroupAttachments;
